/**
 * MobileDetailView Component
 *
 * A mobile app-like detail view for poem/story pages.
 * This component renders when the screen width is <= 768px.
 * It mirrors the UI of the mobile app while keeping all web functionality.
 */

import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { IoChatbubbleOutline } from "react-icons/io5";
import {
  Clock,
  Send,
  Trash,
  MoreVertical,
  Play,
  Pause,
  Globe,
  ChevronDown,
  ChevronRight,
  Upload,
  Eye,
} from "lucide-react";
import { RxLayers } from "react-icons/rx";
import Default from "../Images/Defaultprofile.png";
import { useTheme } from "../hooks/useTheme";
import SindhiFontWoff2 from "../fonts/SHREE-DEV7-0714.woff2";
import SindhiFontWoff from "../fonts/SHREE-DEV7-0714.woff";

const SindhiFontStyles = createGlobalStyle`
  @font-face {
    font-family: 'SHREE-DEV7-0714';
    src:
      url(${SindhiFontWoff2}) format('woff2'),
      url(${SindhiFontWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

const MobileDetailView = ({
  // Content
  content,
  type = "poem",
  category,
  description,

  // User data
  profile,
  myprofile,
  isFollow,

  // Actions
  onLike,
  onFollow,
  onShare,
  onOpenMenu,
  onSubmitComment,
  onCommentLike,
  onReply,
  onDeleteComment,
  onDeleteSubComment,
  onProfileClick,

  // Comments
  usercomments = [],
  comment,
  setComment,
  subComment,
  setSubComment,
  ReplyOpen,
  setReplyOpen,
  openComment,
  setOpenComment,

  // TTS
  ttsLanguages = [],
  selectedTtsLanguage,
  onTtsLanguageChange,
  onTextToSpeech,
  isPlaying,
  progress,
  audioCurrentTime = 0,
  audioDuration = 0,

  // Portfolio
  isOwner,
  present,
  onAddToPortfolio,
  onDeleteFromPortfolio,

  // Refs
  inputRef,

  // Translation
  t,

  // Optional section content
  recommendedSection,
}) => {
  const [imageError, setImageError] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const isSindhiContent = (content?.language || "").toLowerCase() === "sindhi";

  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getProxiedImageUrl = (url) => {
    if (!url || typeof url !== "string") return Default;
    if (
      url.includes("data:image") ||
      url.includes("blob:") ||
      url.startsWith("static/") ||
      url.startsWith("/static") ||
      url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")
    )
      return url;
    return `https://api.storytent.in/api/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatCount = (count) => {
    const n = Number(count) || 0;
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    return String(n);
  };

  const selectedLangName =
    ttsLanguages.find((l) => l.code === selectedTtsLanguage)?.name ||
    "LANGUAGE";
  const visibleComments = showAllComments
    ? usercomments
    : usercomments.slice(0, 2);

  return (
    <GridBackground>
      <SindhiFontStyles />
      <ContentWrapper>
        <WhiteBox>
          {/* Header Section */}
          <HeaderSection>
            <HeaderContentRow>
              {/* Left Column: Profile + Title */}
              <LeftColumn>
                <ProfileRow>
                  <ProfileInfo
                    to={
                      content?.userId === myprofile?._id
                        ? "/profile"
                        : `/userprofile/${content?.userId}`
                    }
                  >
                    {content?.profilePic && !imageError ? (
                      <ProfileImage
                        src={getProxiedImageUrl(content.profilePic)}
                        alt={content.userName}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <ProfileInitial>
                        {getInitials(content?.userName)}
                      </ProfileInitial>
                    )}
                    <AuthorInfo>
                      <NameFollowRow>
                        <AuthorName>
                          {content?.userName || "Anonymous"}
                        </AuthorName>
                      </NameFollowRow>
                    </AuthorInfo>
                  </ProfileInfo>
                  <OwnerActions>
                    {String(content?.userId) === String(myprofile?._id) && (
                      <>
                        <PortfolioToggle
                          onClick={
                            present ? onDeleteFromPortfolio : onAddToPortfolio
                          }
                        >
                          {present ? "-" : "+"}
                        </PortfolioToggle>
                        <MenuButton onClick={onOpenMenu}>
                          <MoreVertical size={18} color="var(--color-text)" />
                        </MenuButton>
                      </>
                    )}
                  </OwnerActions>
                </ProfileRow>

                <TitleSection>
                  <StoryTitle $isSindhi={isSindhiContent}>
                    {content?.name}
                  </StoryTitle>
                </TitleSection>
              </LeftColumn>

              {/* Right Column: Thumbnail */}
              <ThumbnailContainer>
                <Thumbnail
                  src={
                    getProxiedImageUrl(content?.thumbnail) ||
                    "https://cdn-icons-png.flaticon.com/128/16827/16827672.png"
                  }
                  alt="thumbnail"
                  onError={(e) => {
                    e.target.src =
                      "https://cdn-icons-png.flaticon.com/128/16827/16827672.png";
                  }}
                />
              </ThumbnailContainer>
            </HeaderContentRow>
          </HeaderSection>

          {/* Metadata Badges */}
          <MetadataBadgesWrapper>
            {category && (
              <BadgeTag>
                <RxLayers size={11} />
                <span>{category.toUpperCase()}</span>
              </BadgeTag>
            )}
            <BadgeTag>
              <Clock size={11} />
              <span>{content?.duration || 2}M READ</span>
            </BadgeTag>
            <BadgeLangContainer>
              <Globe size={11} />
              <span>{selectedLangName.toUpperCase()}</span>
              <ChevronDown size={11} />
              <LangSelectOverlay
                value={selectedTtsLanguage}
                onChange={onTtsLanguageChange}
              >
                <option value="">Language</option>
                {ttsLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </LangSelectOverlay>
            </BadgeLangContainer>
          </MetadataBadgesWrapper>

          {/* Audio Player Card */}
          <AudioPlayerCard>
            <AudioPlayBtn onClick={onTextToSpeech}>
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </AudioPlayBtn>
            <AudioPlayerInfo>
              <AudioPlayerHeader>
                <AudioLabel>
                  {type === "poem" ? "AUDIO POEM" : "AUDIO STORY"}
                </AudioLabel>
                <AudioTime>
                  {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                </AudioTime>
              </AudioPlayerHeader>
              <AudioProgressBar>
                <AudioProgressFill style={{ width: `${progress}%` }} />
              </AudioProgressBar>
            </AudioPlayerInfo>
          </AudioPlayerCard>

          {/* Quote/Description Section */}
          <QuoteSection>
            <QuoteBorder />
            <DescriptionContainer
              $isSindhi={isSindhiContent}
              dangerouslySetInnerHTML={{
                __html: description || content?.description,
              }}
            />
          </QuoteSection>

          {/* Engagement Row */}
          <EngagementBar>
            <EngagementLeft>
              <EngagementStat onClick={onLike}>
                {content?.userLike ? (
                  <AiFillHeart size={20} color="#FF0000" />
                ) : (
                  <AiOutlineHeart size={20} />
                )}
                <EngagementCount>
                  {formatCount(content?.likes || 0)}
                </EngagementCount>
              </EngagementStat>

              <EngagementStat onClick={() => inputRef?.current?.focus()}>
                <IoChatbubbleOutline size={20} />
                <EngagementCount>
                  {formatCount(usercomments?.length || 0)}
                </EngagementCount>
              </EngagementStat>

              <EngagementStat>
                <Eye size={20} />
                <EngagementCount>
                  {formatCount(content?.totalViews ?? content?.viewCount ?? 0)}
                </EngagementCount>
              </EngagementStat>
            </EngagementLeft>

            <ShareActionBtn onClick={onShare}>
              <Upload size={16} />
              <span>SHARE</span>
            </ShareActionBtn>
          </EngagementBar>

          {/* Comments Section */}
          <CommentsSection>
            {/* Comment Input */}
            <CommentInputWrapper>
              <CommentInputRow>
                {profile?.profilePic ? (
                  <AuthorAvatarSmall
                    src={getProxiedImageUrl(profile.profilePic)}
                    alt="profile"
                  />
                ) : (
                  <AuthorAvatarInitial>
                    {getInitials(profile?.userName) || "ME"}
                  </AuthorAvatarInitial>
                )}
                <TextAreaContainer>
                  <CommentTextArea
                    ref={inputRef}
                    rows={1}
                    placeholder="Share your thoughts..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <SendButton onClick={onSubmitComment}>
                    <ChevronRight size={18} color="var(--color-text-inverse)" />
                  </SendButton>
                </TextAreaContainer>
              </CommentInputRow>
            </CommentInputWrapper>

            {/* Comments List */}
            {Array.isArray(usercomments) &&
              visibleComments.map((data, index) => {
                return (
                  <CommentItem key={data.usercomment._id || index}>
                    {/* Avatar + Bubble */}
                    <CommentBubbleRow>
                      <CommentProfileImage
                        src={getProxiedImageUrl(
                          data.usercomment.userProfilePicture,
                        )}
                      />
                      <CommentBubble>
                        <CommentBubbleHeader>
                          <CommentBubbleAuthor>
                            {data.usercomment.userName}
                          </CommentBubbleAuthor>
                          {myprofile?._id === data.usercomment.userId && (
                            <DeleteComment
                              onClick={() =>
                                onDeleteComment?.(data.usercomment._id)
                              }
                            >
                              <Trash
                                size={14}
                                color="var(--color-text-secondary)"
                              />
                            </DeleteComment>
                          )}
                        </CommentBubbleHeader>
                        <CommentBubbleText>
                          {data.usercomment.description ||
                            data.usercomment.comment}
                        </CommentBubbleText>
                      </CommentBubble>
                    </CommentBubbleRow>

                    {/* Actions below bubble */}
                    <CommentActions>
                      <ActionItem
                        onClick={() => onCommentLike?.(data.usercomment._id)}
                      >
                        <AiOutlineHeart
                          size={15}
                          color={
                            data.usercomment.likes?.some(
                              (l) => l.userId === myprofile?._id,
                            )
                              ? "#F00"
                              : "var(--color-text-secondary)"
                          }
                        />
                        <ActionText>
                          {data.usercomment.likes?.length || 0}
                        </ActionText>
                      </ActionItem>
                      <ActionItem
                        onClick={() =>
                          setReplyOpen?.(
                            ReplyOpen === data.usercomment._id
                              ? ""
                              : data.usercomment._id,
                          )
                        }
                      >
                        <ActionText>REPLY</ActionText>
                      </ActionItem>
                    </CommentActions>

                    {/* Reply Input */}
                    {ReplyOpen === data.usercomment._id && (
                      <ReplyInputWrapper>
                        <ReplyInputControl
                          placeholder="Write a reply..."
                          value={subComment}
                          onChange={(e) => setSubComment(e.target.value)}
                        />
                        <ReplySendButton
                          onClick={() => onReply?.(data.usercomment._id)}
                        >
                          <Send size={16} color="var(--color-primary)" />
                        </ReplySendButton>
                      </ReplyInputWrapper>
                    )}

                    {/* Sub Comments with toggle inside */}
                    {data.usersubcomments?.length > 0 && (
                      <SubCommentsWrapper>
                        <ViewRepliesText
                          onClick={() =>
                            setOpenComment?.(
                              openComment === data.usercomment._id
                                ? ""
                                : data.usercomment._id,
                            )
                          }
                        >
                          {openComment === data.usercomment._id
                            ? "Hide Replies"
                            : `Show Replies (${data.usersubcomments.length})`}
                        </ViewRepliesText>
                        {openComment === data.usercomment._id &&
                          Array.isArray(data.usersubcomments) &&
                          data.usersubcomments.map((subData, subIndex) => (
                            <SubCommentItem key={subData._id || subIndex}>
                              <CommentBubbleRow>
                                <SubCommentProfileImage
                                  src={getProxiedImageUrl(
                                    subData.userProfilePicture,
                                  )}
                                />
                                <CommentBubble>
                                  <CommentBubbleHeader>
                                    <CommentBubbleAuthor>
                                      {subData.userName}
                                    </CommentBubbleAuthor>
                                    {myprofile?._id === subData.userId && (
                                      <DeleteComment
                                        onClick={() =>
                                          onDeleteSubComment?.(subData._id)
                                        }
                                      >
                                        <Trash
                                          size={13}
                                          color="var(--color-text-secondary)"
                                        />
                                      </DeleteComment>
                                    )}
                                  </CommentBubbleHeader>
                                  <CommentBubbleText
                                    style={{ fontSize: "13px" }}
                                  >
                                    {subData.description || subData.comment}
                                  </CommentBubbleText>
                                </CommentBubble>
                              </CommentBubbleRow>
                              <CommentActions style={{ paddingLeft: "38px" }}>
                                <ActionItem
                                  onClick={() => onCommentLike?.(subData._id)}
                                >
                                  <AiOutlineHeart
                                    size={13}
                                    color={
                                      subData.likes?.some(
                                        (l) => l.userId === myprofile?._id,
                                      )
                                        ? "#F00"
                                        : "var(--color-text-secondary)"
                                    }
                                  />
                                  <ActionText style={{ fontSize: "11px" }}>
                                    {subData.likes?.length || 0}
                                  </ActionText>
                                </ActionItem>
                              </CommentActions>
                            </SubCommentItem>
                          ))}
                      </SubCommentsWrapper>
                    )}
                  </CommentItem>
                );
              })}

            {/* View All Comments Button */}
            {usercomments.length > 2 && !showAllComments && (
              <ViewAllCommentsBtn onClick={() => setShowAllComments(true)}>
                VIEW ALL {usercomments.length} COMMENTS
              </ViewAllCommentsBtn>
            )}
          </CommentsSection>
          {recommendedSection}
        </WhiteBox>
      </ContentWrapper>
    </GridBackground>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const GridBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: var(--color-background);
  position: relative;
  background-image: linear-gradient(var(--color-border) 1px, transparent 1px);
  background-size: 100% 25px;
  padding-bottom: 30px;
`;

const ContentWrapper = styled.div`
  padding: 20px 10px;
  position: relative;
  z-index: 1;

  @media (min-width: 480px) {
    padding: 30px 20px;
  }
`;

const WhiteBox = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

  @media (min-width: 480px) {
    padding: 24px;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 20px;
`;

const HeaderContentRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const LeftColumn = styled.div`
  flex: 1;
  padding-right: 8px;
`;

const ProfileRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ProfileInfo = styled(Link)`
  display: flex;
  align-items: flex-start;
  flex: 1;
  text-decoration: none;
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 10px;
  object-fit: cover;
  flex-shrink: 0;

  @media (min-width: 480px) {
    width: 48px;
    height: 48px;
    border-radius: 24px;
    margin-right: 12px;
  }
`;

const ProfileInitial = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 10px;
  background: #465c4c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;

  @media (min-width: 480px) {
    width: 48px;
    height: 48px;
    border-radius: 24px;
    margin-right: 12px;
    font-size: 16px;
  }
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const NameFollowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
`;

const AuthorName = styled.span`
  font-size: 14px;
  color: var(--color-text-tertiary) !important;
  font-weight: 700;
  font-family: "Inter", sans-serif;

  @media (min-width: 480px) {
    font-size: 15px;
  }
`;

const OwnerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PortfolioToggle = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid var(--color-primary);
  background: var(--color-surface);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    background: var(--color-primary);
    color: #ffffff;
  }
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const StoryTitle = styled.h1`
  font-size: 1.7rem;
  color: var(--color-text) !important;
  margin-bottom: 4px;
  font-weight: 800;
  line-height: 1.15;
  font-family: ${(props) =>
    props.$isSindhi
      ? "'SHREE-DEV7-0714', 'Inter', sans-serif"
      : "'Inter', sans-serif"};
  ${(props) => (props.$isSindhi ? "font-size: 1.5rem;" : "")}

  @media (min-width: 480px) {
    font-size: ${(props) => (props.$isSindhi ? "1.7rem" : "2rem")};
  }
`;

const ThumbnailContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: 480px) {
    width: 100px;
    height: 100px;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// ─── Metadata Badges ─────────────────────────────────────────────────────────

const MetadataBadgesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const BadgeTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--color-primary);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: default;
  background-color: #f9fafc;

  svg {
    color: var(--color-primary);
    flex-shrink: 0;
  }
`;

const BadgeLangContainer = styled(BadgeTag)`
  position: relative;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const LangSelectOverlay = styled.select`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  font-size: 12px;
`;

// ─── Audio Player ─────────────────────────────────────────────────────────────

const AudioPlayerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f9fafc;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 20px;
`;

const AudioPlayBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--color-primary);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: #ffffff;

  &:hover {
    opacity: 0.85;
  }

  svg {
    color: #ffffff;
  }
`;

const AudioPlayerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AudioPlayerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const AudioLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-primary);
  text-transform: uppercase;
`;

const AudioTime = styled.span`
  font-size: 10px;
  color: var(--color-text-secondary);
  font-weight: 500;
  white-space: nowrap;
`;

const AudioProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  overflow: hidden;
`;

const AudioProgressFill = styled.div`
  height: 100%;
  background: var(--color-primary);
  border-radius: 2px;
  transition: width 0.3s ease;
`;

// ─── Quote / Description ─────────────────────────────────────────────────────

const QuoteSection = styled.div`
  margin: 20px 0;
  position: relative;
  display: flex;
`;

const QuoteBorder = styled.div`
  width: 4px;
  background-color: var(--color-border);
  margin-right: 16px;
  border-radius: 2px;
`;

const DescriptionContainer = styled.div`
  flex: 1;
  font-family: ${(props) =>
    props.$isSindhi
      ? "'SHREE-DEV7-0714', 'Georgia', serif"
      : "'Georgia', serif"};
  font-size: ${(props) => (props.$isSindhi ? "17px" : "15px")};
  line-height: 1.8;
  color: var(--color-text) !important;
  -webkit-text-fill-color: var(--color-text) !important;

  ${(props) =>
    props.$isSindhi
      ? `
    p, span, strong, em, u {
      font-family: 'SHREE-DEV7-0714', 'Georgia', serif;
      font-size: 17px;
    }
  `
      : ""}

  img {
    max-width: 100%;
    height: auto;
  }
`;

// ─── Engagement Row ───────────────────────────────────────────────────────────

const EngagementBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  padding: 10px 0;
  margin-bottom: 24px;
`;

const EngagementLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EngagementStat = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: var(--color-text-secondary);

  svg {
    color: var(--color-text-secondary);
  }

  &:hover {
    color: var(--color-text);

    svg {
      color: var(--color-text);
    }
  }
`;

const EngagementCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: inherit;
`;

const ShareActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  flex-shrink: 0;
  padding: 4px 0;

  svg {
    color: var(--color-text-secondary);
  }

  &:hover {
    color: var(--color-text);
    svg {
      color: var(--color-text);
    }
  }
`;

// ─── Comments ─────────────────────────────────────────────────────────────────

const CommentsSection = styled.div`
  margin-top: 24px;
`;

const CommentInputWrapper = styled.div`
  margin-bottom: 24px;
`;

const CommentInputRow = styled.div`
  display: flex;
  gap: 12px;
`;

const AuthorAvatarSmall = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const AuthorAvatarInitial = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-border);
  color: var(--color-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
`;

const TextAreaContainer = styled.div`
  flex: 1;
  position: relative;
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  border: 1px solid var(--color-border);
  background: var(--color-background);
  color: var(--color-text);
  border-radius: 8px;
  padding: 12px 48px 12px 12px;
  font-family: inherit;
  font-size: 13px;
  min-height: 48px;
  max-height: 150px;
  outline: none;
  resize: none;
  box-sizing: border-box;

  &::placeholder {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  &:focus {
    border-color: var(--color-primary);
  }

  @media (min-width: 480px) {
    font-size: 14px;
    &::placeholder {
      font-size: 14px;
    }
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 10px;
  bottom: 10px;
  background: var(--color-primary);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ResponsesTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 20px;
`;

const CommentItem = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border);
`;

const CommentBubbleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
`;

const CommentBubble = styled.div`
  flex: 1;
  background-color: #f9fafc;
  border-radius: 12px;
  padding: 9px 13px;
`;

const CommentBubbleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const CommentBubbleAuthor = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--color-text-tertiary);
`;

const CommentBubbleText = styled.p`
  font-size: 13px;
  color: var(--color-text-tertiary);
  line-height: 1.45;
  margin: 0;
`;

const CommentProfileImage = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const DeleteComment = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const CommentActions = styled.div`
  display: flex;
  gap: 14px;
  padding-left: 44px;
  margin-bottom: 6px;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const ActionText = styled.span`
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ReplyInputWrapper = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 8px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 4px 8px;
`;

const ReplyInputControl = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  padding: 6px;
  outline: none;
  color: var(--color-text);
`;

const ReplySendButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
`;

const ViewRepliesText = styled.div`
  font-size: 10px;
  color: var(--color-text-secondary);
  font-weight: 500;
  cursor: pointer;
  margin-top: 0;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }
`;

const ViewAllCommentsBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  background: #f9fafc;
  border: none;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.7px;
  color: var(--color-text-secondary);
  cursor: pointer;
  text-transform: uppercase;

  &:hover {
    background: #e4e6e9;
    color: var(--color-text);
  }
`;

const SubCommentItem = styled.div`
  margin-top: 10px;
`;

const SubCommentHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const SubCommentsWrapper = styled.div`
  margin-left: 44px;
  padding-left: 10px;
  border-left: 2px solid var(--color-border);
  margin-top: 6px;
`;

const SubCommentProfileImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const SubCommentText = styled.p`
  font-size: 13px;
  color: var(--color-text-secondary) !important;
  margin: 4px 0 0 38px;
`;

export default MobileDetailView;
